import {
  Box,
  Button,
  ButtonBase,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { gql } from "@apollo/client";
import { orderBy } from "lodash";
import { useMemo, useState } from "react";
import { format } from "date-fns";

import {
  GetVisitDetailsQuery,
  useCustomUpdateErrandStatusMutation,
  useCustomUpdateVisitStatusMutation,
  VisitEventStatusEnum,
  VisitResourceErrandStatusEnum,
} from "~/graphql/__generated__/graphql";
import { displayStatus, SharedVisitCalendarStatus, StatusBgMap, StatusForegroundMap } from "~/utils/statuses";
import { VisitStatuses } from "@caredna-marlin/utils/client";
import VisitStatusChip from "./VisitStatusChip";

gql`
  mutation CustomUpdateVisitStatus(
    $visitId: Int!
    $newEventStatus: VisitEventStatusEnum!
    $autoGenerated: Boolean!
    $eventTime: String
  )
  @permission(requires: manageVisitStatus)
  {
    updateVisit(
      where: { id: { eq: $visitId } }
      set: { eventStatus: $newEventStatus }
      autoGenerated: $autoGenerated
      eventTime: $eventTime
    ) {
      id
      eventStatus
    }
  }
`;

gql`
  mutation CustomUpdateErrandStatus(
    $id: Int!
    $visitId: Int!
    $newStatus: VisitResourceErrandStatusEnum!
    $autoGenerated: Boolean!
    $eventTime: String
  )
  @permission(requires: manageVisitStatus, check: [userInVisitServiceLine])
  {
    updateErrandVisitResourceStatus(
      id: $id
      visitId: $visitId
      newStatus: $newStatus
      autoGenerated: $autoGenerated
      eventTime: $eventTime
    ) {
      id
      errandStatus
    }
  }
`;

type Visit = NonNullable<GetVisitDetailsQuery["visitSingle"]>;

type Props = {
  visit: Visit;
  onStatusChanged?: () => void;
};

const VISIT_TARGET = "visit";

const STATUS_PROGRESSION: SharedVisitCalendarStatus[] = Object.values(VisitStatuses).filter(
  (s) => s !== VisitStatuses.canceled
);

const isClosedStatus = (status?: string | null) => (
  status === VisitEventStatusEnum.Canceled || status === VisitEventStatusEnum.Complete
);

const getAvailableStatuses = (currentStatus?: SharedVisitCalendarStatus): SharedVisitCalendarStatus[] => {
  if (isClosedStatus(currentStatus)) {
    return [];
  }

  const currentIndex = currentStatus ? STATUS_PROGRESSION.indexOf(currentStatus) : -1;

  // Return only statuses that come AFTER the current one
  return STATUS_PROGRESSION.slice(currentIndex + 1);
};

const defaultNextStatus = (currentStatus?: SharedVisitCalendarStatus): SharedVisitCalendarStatus | "" => {
  const availableStatuses = getAvailableStatuses(currentStatus);
  return availableStatuses.length === 1 ? availableStatuses[0] : "";
};

const borderlessSelectSx = {
  minWidth: "auto",
  height: "auto",
  bgcolor: "transparent",
  "& .MuiSelect-select": {
    py: 0,
    px: 0,
    pr: "20px !important",
    display: "flex",
    alignItems: "center",
    bgcolor: "transparent",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-icon": {
    color: "text.secondary",
    right: 0,
    fontSize: "18px",
  },
};

const ColoredMenuItem = ({ status }: { status: SharedVisitCalendarStatus }) => {
  const bgColor = StatusBgMap[status];
  const fgColor = StatusForegroundMap[status];
  const label = displayStatus(status, "visit");

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.25,
        borderRadius: "4px",
        backgroundColor: bgColor,
        color: fgColor,
        fontWeight: 500,
        fontSize: "12px",
      }}
    >
      {label}
    </Box>
  );
};

const formatDateTimeLocal = (date: Date): string => {
  return format(date, "yyyy-MM-dd'T'HH:mm");
};

const clampToNow = (localValue: string): string => {
  const parsed = new Date(localValue);
  if (Number.isNaN(parsed.getTime())) return formatDateTimeLocal(new Date());

  const now = new Date();
  if (parsed.getTime() > now.getTime()) return formatDateTimeLocal(now);

  return localValue;
};

const VisitStatusChangeForm = ({
  visit,
  onStatusChanged,
}: Props) => {
  const errands = useMemo(
    () => orderBy((visit.visitResources ?? []).filter(resource => resource.errandStatus), "startTime"),
    [visit.visitResources],
  );
  const selectableErrands = useMemo(
    () => errands.filter(resource => !isClosedStatus(resource.errandStatus)),
    [errands],
  );
  const visitIsSelectable = !isClosedStatus(visit.eventStatus);
  const selectableTargetIds = useMemo(
    () => [
      ...(visitIsSelectable ? [VISIT_TARGET] : []),
      ...selectableErrands.map(resource => String(resource.id)),
    ],
    [visitIsSelectable, selectableErrands],
  );
  const [targetId, setTargetId] = useState<string | undefined>(undefined);
  const [statusByTarget, setStatusByTarget] = useState<Record<string, SharedVisitCalendarStatus | "">>({});
  const resolvedTargetId = targetId && selectableTargetIds.includes(targetId)
    ? targetId
    : selectableTargetIds[0];
  const selectedErrand = selectableErrands.find(resource => String(resource.id) === resolvedTargetId);
  const currentStatus: SharedVisitCalendarStatus | undefined =
    selectedErrand?.errandStatus ?? visit.eventStatus;
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [customEventTime, setCustomEventTime] = useState(formatDateTimeLocal(new Date()));
  const [timePopoverAnchor, setTimePopoverAnchor] = useState<HTMLElement | null>(null);

  const [customUpdateVisitStatus, visitState] = useCustomUpdateVisitStatusMutation();
  const [customUpdateErrandStatus, errandState] = useCustomUpdateErrandStatusMutation();
  const loading = visitState.loading || errandState.loading;

  const availableStatuses = getAvailableStatuses(currentStatus);
  const selectedStatus = resolvedTargetId
    ? (statusByTarget[resolvedTargetId] ?? defaultNextStatus(currentStatus))
    : "";

  const maxLocalNow = formatDateTimeLocal(new Date());
  const refetchQueries = ["GetVisitDetails", "CommandIndexCombinedData"];

  const handleTargetChange = (nextTargetId: string) => {
    setTargetId(nextTargetId);
  };

  const handleSubmit = async () => {
    if (!selectedStatus) return;

    let eventTimeIso: string | null = null;

    if (useCustomTime) {
      const clampedLocal = clampToNow(customEventTime);
      const dt = new Date(clampedLocal);

      if (Number.isNaN(dt.getTime())) {
        eventTimeIso = new Date().toISOString();
      } else {
        eventTimeIso = dt.toISOString();
      }
    }

    if (selectedErrand) {
      await customUpdateErrandStatus({
        variables: {
          id: selectedErrand.id,
          visitId: visit.id,
          newStatus: (selectedStatus satisfies SharedVisitCalendarStatus) as VisitResourceErrandStatusEnum,
          autoGenerated: false,
          eventTime: eventTimeIso,
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
    } else {
      await customUpdateVisitStatus({
        variables: {
          visitId: visit.id,
          newEventStatus: (selectedStatus satisfies SharedVisitCalendarStatus) as VisitEventStatusEnum,
          autoGenerated: false,
          eventTime: eventTimeIso,
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
    }

    if (resolvedTargetId) {
      setStatusByTarget(previous => {
        const next = { ...previous };
        delete next[resolvedTargetId];
        return next;
      });
    }

    onStatusChanged?.();
  };

  const handleTimeIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setTimePopoverAnchor(event.currentTarget);
  };

  const handleTimePopoverClose = () => {
    setTimePopoverAnchor(null);
  };

  const handleSetCustomTime = () => {
    setUseCustomTime(true);
    handleTimePopoverClose();
  };

  const handleUseNow = () => {
    setUseCustomTime(false);
    setCustomEventTime(formatDateTimeLocal(new Date()));
    handleTimePopoverClose();
  };

  if (selectableTargetIds.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        py: 1,
        px: 1.5,
        bgcolor: "grey.50",
        borderRadius: 1,
        mb: 2,
        border: "1px solid",
        borderColor: "grey.200",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        flexWrap: "wrap",
      }}
    >
      {selectableErrands.length > 0 ? (
        <>
          <Typography component="span" sx={{ color: "text.secondary", fontSize: "12px" }}>
            Advance
          </Typography>
          <Select
            value={resolvedTargetId}
            onChange={event => handleTargetChange(String(event.target.value))}
            size="small"
            IconComponent={KeyboardArrowDownIcon}
            disabled={loading}
            aria-label="Status target"
            renderValue={value => (
              <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                {value === VISIT_TARGET
                  ? "Visit"
                  : selectableErrands.find(resource => String(resource.id) === value)?.resourceRequirement?.errandType?.name
                    ?? "Errand"}
              </Typography>
            )}
            sx={borderlessSelectSx}
            MenuProps={{
              PaperProps: {
                sx: { mt: 0.5, borderRadius: "6px" },
              },
            }}
          >
            {visitIsSelectable && (
              <MenuItem value={VISIT_TARGET} sx={{ py: 0.75, px: 1, minHeight: "auto", fontSize: "12px" }}>
                Visit
              </MenuItem>
            )}
            {selectableErrands.map(resource => (
              <MenuItem
                key={resource.id}
                value={String(resource.id)}
                sx={{ py: 0.75, px: 1, minHeight: "auto", fontSize: "12px" }}
              >
                {resource.resourceRequirement?.errandType?.name ?? "Errand"}
              </MenuItem>
            ))}
          </Select>
          <Typography component="span" sx={{ color: "text.secondary", fontSize: "12px" }}>
            status from
          </Typography>
        </>
      ) : (
        <Typography component="span" sx={{ color: "text.secondary", fontSize: "12px" }}>
          Advance status from
        </Typography>
      )}
      {currentStatus && <VisitStatusChip status={currentStatus} sx={{ fontSize: "11px", height: "20px" }} />}
      <Typography component="span" sx={{ color: "text.secondary", fontSize: "12px" }}>
        to
      </Typography>

      <Select
        value={selectedStatus}
        onChange={(e) => {
          if (!resolvedTargetId) return;
          setStatusByTarget(previous => ({
            ...previous,
            [resolvedTargetId]: e.target.value as SharedVisitCalendarStatus,
          }));
        }}
        displayEmpty
        size="small"
        IconComponent={KeyboardArrowDownIcon}
        disabled={loading}
        aria-label="New status"
        renderValue={(value) =>
          value ? <ColoredMenuItem status={value as SharedVisitCalendarStatus} /> : (
            <Typography sx={{ fontSize: "12px", color: "text.secondary", bgcolor: "grey.200", px: 1, py: 0.25, borderRadius: "4px" }}>Select...</Typography>
          )
        }
        sx={borderlessSelectSx}
        MenuProps={{
          PaperProps: {
            sx: { mt: 0.5, borderRadius: "6px" },
          },
        }}
      >
        {availableStatuses.map((status) => (
          <MenuItem key={status} value={status} sx={{ py: 0.75, px: 1, minHeight: "auto" }}>
            <ColoredMenuItem status={status} />
          </MenuItem>
        ))}
      </Select>

      {selectedStatus && (
        <>
          <Typography component="span" sx={{ color: "text.secondary", fontSize: "12px" }}>
            at
          </Typography>
          <Tooltip title={useCustomTime ? `Custom: ${customEventTime}` : "Click to change time"}>
            <ButtonBase
              onClick={handleTimeIconClick}
              sx={{
                fontSize: "12px",
                color: useCustomTime ? "primary.main" : "text.secondary",
                textDecoration: "underline",
                textDecorationStyle: "dotted",
                "&:hover": { color: "primary.main" },
                mr: 1
              }}
            >
              {useCustomTime ? format(new Date(customEventTime), "h:mm a") : "Now"}
            </ButtonBase>
          </Tooltip>

          <Popover
            open={Boolean(timePopoverAnchor)}
            anchorEl={timePopoverAnchor}
            onClose={handleTimePopoverClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1, minWidth: 200 }}>
              <TextField
                type="datetime-local"
                size="small"
                label="Event Time"
                value={customEventTime}
                slotProps={{
                  htmlInput: {
                    max: maxLocalNow,
                  },
                }}
                onChange={(e) => {
                  const clampedLocal = clampToNow(e.target.value);
                  setCustomEventTime(clampedLocal);
                  setUseCustomTime(true);
                }}
                fullWidth
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" variant="outlined" onClick={handleUseNow} fullWidth sx={{ fontSize: "11px" }}>
                  Now
                </Button>
                <Button size="small" variant="contained" onClick={handleSetCustomTime} fullWidth sx={{ fontSize: "11px" }}>
                  Set
                </Button>
              </Box>
            </Box>
          </Popover>

          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={loading || !selectedStatus}
            loading={loading}
            sx={{
              height: 24,
              minWidth: 50,
              fontSize: "11px",
              textTransform: "none",
              px: 1,
            }}
          >
            Update Status
          </Button>
        </>
      )}
    </Box>
  );
};

export default VisitStatusChangeForm;
